import React, { useState, useEffect } from 'react';
import axios from 'axios';
import MultiplayerLobby from './MultiplayerLobbyNew';
import MultiplayerKBC from './MultiplayerKBC';
import KBCMultiplexMode from './KBCMultiplexMode';
import KBCMultiplexMultiplayer from './KBCMultiplexMultiplayer';
import KBCHostContestant from './KBCHostContestant';
import TeacherStudentKBC from './TeacherStudentKBC';
import { AUDIO_CONFIG } from '../../../config/audioConfig';
import './QuizGame.css';

const QuizGame = ({ userRole, userClass, userName, userEmail, userId, onExit }) => {
  const [gameState, setGameState] = useState('lobby');
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [score, setScore] = useState(0);
  const [totalPoints, setTotalPoints] = useState(0);
  const [timeLeft, setTimeLeft] = useState(30);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [showResult, setShowResult] = useState(false);
  const [showPrizeLadder, setShowPrizeLadder] = useState(false);
  const [animationClass, setAnimationClass] = useState('');
  const [lifelines, setLifelines] = useState({
    fiftyFifty: true,
    skip: true,
    hint: true
  });
  const [gameSession, setGameSession] = useState(null);

  const prizeAmounts = [100, 200, 500, 1000, 2000, 5000, 10000, 20000, 50000, 100000, 500000, 1000000];

  // Audio management system
  const [currentAudio, setCurrentAudio] = useState(null);
  const [gameStarted, setGameStarted] = useState(false);
  const [timerAudio, setTimerAudio] = useState(null);

  // Stop current audio and play new one
  const playAudio = (audioUrl) => {
    try {
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
      }
      
      console.log('Playing audio:', audioUrl);
      const audio = new Audio(audioUrl);
      audio.volume = 0.5;
      setCurrentAudio(audio);
      
      audio.play().catch(error => console.log('Audio play failed:', error));
      audio.onended = () => setCurrentAudio(null);
    } catch (error) {
      console.log('Audio not supported:', error);
    }
  };

  // Stop current audio immediately
  const stopAudio = () => {
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
      setCurrentAudio(null);
    }
    if (timerAudio) {
      timerAudio.pause();
      timerAudio.currentTime = 0;
      setTimerAudio(null);
    }
  };

  // Play timer audio specifically
  const playTimerAudio = () => {
    try {
      if (timerAudio) {
        timerAudio.pause();
        timerAudio.currentTime = 0;
      }
      
      const audio = new Audio(AUDIO_CONFIG.TIMER_SOUND);
      audio.volume = 0.5;
      audio.loop = true;
      setTimerAudio(audio);
      
      audio.play().catch(error => console.log('Timer audio play failed:', error));
    } catch (error) {
      console.log('Timer audio not supported:', error);
    }
  };

  // Stop timer audio immediately
  const stopTimerAudio = () => {
    if (timerAudio) {
      timerAudio.pause();
      timerAudio.currentTime = 0;
      setTimerAudio(null);
    }
  };

  // Enhanced sound effects with MP3 files
  const playSound = (type) => {
    console.log('playSound called with type:', type);
    switch(type) {
      case 'start':
        if (!gameStarted) {
          console.log('Playing start sound, gameStarted:', gameStarted);
          playAudio(AUDIO_CONFIG.START_SOUND);
          setGameStarted(true);
        }
        break;
      case 'timer':
        console.log('Playing timer sound');
        playTimerAudio();
        break;
      case 'question':
        console.log('Playing question sound');
        stopTimerAudio(); // Stop timer immediately
        playAudio(AUDIO_CONFIG.QUESTION_SOUND);
        break;
      case 'lifeline':
        console.log('Playing lifeline sound');
        stopTimerAudio(); // Stop timer immediately
        playAudio(AUDIO_CONFIG.LIFELINE_SOUND);
        break;
      default:
        try {
          const audioContext = new (window.AudioContext || window.webkitAudioContext)();
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();
          
          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);
          
          if (type === 'correct') {
            oscillator.frequency.setValueAtTime(523.25, audioContext.currentTime);
            oscillator.frequency.setValueAtTime(659.25, audioContext.currentTime + 0.1);
            oscillator.frequency.setValueAtTime(783.99, audioContext.currentTime + 0.2);
          } else if (type === 'wrong') {
            oscillator.frequency.setValueAtTime(220, audioContext.currentTime);
            oscillator.frequency.setValueAtTime(196, audioContext.currentTime + 0.2);
          }
          
          gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
          
          oscillator.start(audioContext.currentTime);
          oscillator.stop(audioContext.currentTime + 0.5);
        } catch (error) {
          console.log('Audio not supported');
        }
    }
  };

  useEffect(() => {
    if (gameState === 'playing' && timeLeft > 0) {
      if (timeLeft === 30) {
        // Start timer immediately when new question begins
        setTimeout(() => playSound('timer'), 0);
      }
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      stopAudio();
      handleTimeUp();
    }
  }, [timeLeft, gameState]);

  // Stop timer sound immediately when answer is submitted
  useEffect(() => {
    if (showResult) {
      stopTimerAudio(); // Stop timer immediately
    }
  }, [showResult]);

  // Play start sound when game begins (only once)
  useEffect(() => {
    if (gameState === 'playing' && questions.length > 0 && currentQuestion === 0 && !gameStarted) {
      playSound('start');
    }
  }, [gameState, questions.length, currentQuestion, gameStarted]);

  const handleStartGame = (sessionData) => {
    setGameSession(sessionData);
    
    if (sessionData.mode === 'multiplayer-kbc') {
      // For multiplayer KBC, questions are already provided
      setQuestions(sessionData.gameSession.questions || []);
      setGameState('multiplayer-kbc');
      setTimeLeft(30);
    } else if (sessionData.mode === 'kbc-multiplex') {
      // For KBC Multiplex Mode, questions are already selected by teacher
      setQuestions(sessionData.questions || []);
      setGameState('kbc-multiplex');
    } else if (sessionData.mode === 'kbc-multiplex-multiplayer') {
      // For KBC Multiplex Multiplayer Mode
      setGameSession(sessionData);
      setQuestions(sessionData.questions || []);
      setGameState('kbc-multiplex-multiplayer');
    } else if (sessionData.mode === 'kbc-host-contestant') {
      // For KBC Host-Contestant Mode
      setGameSession(sessionData);
      setQuestions(sessionData.questions || []);
      setGameState('kbc-host-contestant');
    } else if (sessionData.mode === 'teacher-student-kbc') {
      // For Teacher-Student KBC Mode
      setGameSession({
        ...sessionData,
        gameSession: {
          id: `teacher-student-${Date.now()}`,
          questions: sessionData.questions,
          players: [{ name: userName, role: userRole }],
          status: 'waiting'
        }
      });
      setGameState('teacher-student-kbc');
    } else {
      startGame(sessionData.config);
    }
  };

  const startGame = async (config) => {
    try {
      let response;
      
      if (config.questionPaper) {
        // Use specific question paper
        response = await axios.get(`/api/quiz/question-papers/${config.questionPaper}/questions`, {
          params: { limit: 12 }
        });
      } else {
        // Use general questions with source preference
        const params = {
          subject: config.subject,
          class: config.class,
          limit: 12,
          source: config.source || 'both' // 'papers', 'quiz', or 'both'
        };
        const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        response = await axios.get(`${API_BASE}/api/quiz/questions`, { params });
      }
      
      if (response.data && response.data.length > 0) {
        setQuestions(response.data);
        setGameState('playing');
        setTimeLeft(30);
      } else {
        alert('No questions found. Please contact your teacher to add questions.');
        setGameState('lobby');
      }
    } catch (error) {
      alert('No questions available. Please contact your teacher to add questions.');
      setGameState('lobby');
    }
  };

  const handleAnswerSelect = (answer) => {
    setSelectedAnswer(answer);
  };

  const submitAnswer = () => {
    // Stop timer immediately when Final Answer is clicked
    stopTimerAudio();
    
    const correct = selectedAnswer === questions[currentQuestion].correctAnswer;
    
    if (correct) {
      setScore(score + prizeAmounts[currentQuestion]);
      setTotalPoints(totalPoints + prizeAmounts[currentQuestion]);
      setAnimationClass('correct-answer');
      playSound('correct');
    } else {
      setAnimationClass('wrong-answer');
      playSound('wrong');
    }
    
    setShowResult(true);
    setTimeout(() => {
      if (correct && currentQuestion < questions.length - 1) {
        setShowPrizeLadder(true);
      } else {
        endGame();
      }
    }, 2000);
  };

  const continueToPrizeLadder = () => {
    setShowPrizeLadder(false);
    setCurrentQuestion(currentQuestion + 1);
    setSelectedAnswer('');
    setShowResult(false);
    setAnimationClass('');
    setTimeLeft(30);
    // Play question sound for new question
    setTimeout(() => playSound('question'), 100);
  };

  const handleTimeUp = () => {
    setAnimationClass('time-up');
    setShowResult(true);
    setTimeout(() => endGame(), 2000);
  };

  const endGame = async () => {
    try {
      if (gameSession) {
        await axios.post('/api/quiz/scores', {
          score,
          questionsAnswered: currentQuestion + 1,
          subject: gameSession.config.subject,
          class: gameSession.config.class
        });
      }
    } catch (error) {
      console.error('Error saving score:', error);
    }
    setGameState('finished');
  };

  const useFiftyFifty = () => {
    if (!lifelines.fiftyFifty) return;
    
    playSound('lifeline');
    
    const currentQ = questions[currentQuestion];
    const wrongAnswers = currentQ.options.filter(opt => opt !== currentQ.correctAnswer);
    const toRemove = wrongAnswers.slice(0, 2);
    
    setQuestions(prev => {
      const updated = [...prev];
      updated[currentQuestion] = {
        ...updated[currentQuestion],
        options: updated[currentQuestion].options.filter(opt => !toRemove.includes(opt))
      };
      return updated;
    });
    
    setLifelines(prev => ({ ...prev, fiftyFifty: false }));
    setAnimationClass('lifeline-used');
    setTimeout(() => setAnimationClass(''), 1000);
  };

  const useSkip = () => {
    if (!lifelines.skip || currentQuestion >= questions.length - 1) return;
    
    playSound('lifeline');
    
    setCurrentQuestion(currentQuestion + 1);
    setSelectedAnswer('');
    setTimeLeft(30);
    setLifelines(prev => ({ ...prev, skip: false }));
    setAnimationClass('lifeline-used');
    setTimeout(() => setAnimationClass(''), 1000);
  };

  const useHint = () => {
    if (!lifelines.hint) return;
    
    playSound('lifeline');
    alert(questions[currentQuestion].hint || "No hint available for this question");
    setLifelines(prev => ({ ...prev, hint: false }));
    setAnimationClass('lifeline-used');
    setTimeout(() => setAnimationClass(''), 1000);
  };

  if (gameState === 'lobby') {
    return (
      <MultiplayerLobby
        userRole={userRole}
        userClass={userClass}
        userName={userName || "Player"}
        userEmail={userEmail}
        userId={userId}
        onStartGame={handleStartGame}
        onExit={onExit}
      />
    );
  }

  if (gameState === 'multiplayer-kbc') {
    return (
      <MultiplayerKBC
        gameSession={gameSession.gameSession}
        userRole={userRole}
        userName={userName || "Player"}
        userClass={userClass}
        onExit={onExit}
      />
    );
  }

  if (gameState === 'kbc-multiplex') {
    return (
      <KBCMultiplexMode
        userRole={userRole}
        userName={userName || "Player"}
        userClass={userClass}
        selectedQuestions={questions}
        gameConfig={gameSession?.config}
        onExit={onExit}
      />
    );
  }

  if (gameState === 'kbc-multiplex-multiplayer') {
    return (
      <KBCMultiplexMultiplayer
        gameSession={gameSession}
        userRole={userRole}
        userName={userName || "Player"}
        userClass={userClass}
        onExit={onExit}
      />
    );
  }

  if (gameState === 'kbc-host-contestant') {
    return (
      <KBCHostContestant
        gameSession={gameSession}
        userRole={userRole}
        userName={userName || "Player"}
        userClass={userClass}
        onExit={onExit}
      />
    );
  }

  if (gameState === 'teacher-student-kbc') {
    return (
      <TeacherStudentKBC
        gameSession={gameSession.gameSession}
        userRole={userRole}
        userName={userName || "Player"}
        userClass={userClass}
        userId={userId}
        onExit={onExit}
      />
    );
  }

  if (showPrizeLadder) {
    return (
      <div className="prize-ladder-screen">
        <div className="prize-ladder-container">
          <h2>Prize Ladder</h2>
          <div className="ladder">
            {prizeAmounts.map((amount, index) => (
              <div 
                key={index} 
                className={`ladder-level ${
                  index === currentQuestion ? 'current-level' : 
                  index === prizeAmounts.length - 1 ? 'top-prize' : 
                  index < currentQuestion ? 'completed' : ''
                }`}
              >
                <span className="level-number">{index + 1}</span>
                <span className="level-points">{amount.toLocaleString()} Points</span>
              </div>
            ))}
          </div>
          <button className="continue-btn" onClick={continueToPrizeLadder}>
            Tap to Continue
          </button>
        </div>
      </div>
    );
  }

  if (gameState === 'finished') {
    return (
      <div className="quiz-finished">
        <div className="result-container">
          <h2>Game Over!</h2>
          <div className="final-score">
            <span className="score-label">Final Score:</span>
            <span className="score-value">{score.toLocaleString()} Points</span>
          </div>
          <div className="stats">
            <p>Questions Answered: {currentQuestion + 1}/{questions.length}</p>
            <p>Subject: {gameSession?.config?.subject || 'N/A'}</p>
            <p>Class: {gameSession?.config?.class || 'N/A'}</p>
            {gameSession?.config?.questionPaper && (
              <p>Paper: {questions[0]?.paperTitle || 'Question Paper'}</p>
            )}
          </div>
          <button onClick={onExit} className="exit-btn">Back to Games</button>
        </div>
      </div>
    );
  }

  const currentQ = questions[currentQuestion];
  if (!currentQ) return <div>Loading...</div>;

  return (
    <div className={`quiz-game ${animationClass}`}>
      <div className="game-header">
        <div className="points-display">
          <div className="coin-animation">
            🪙 {totalPoints.toLocaleString()} Points
          </div>
        </div>
        
        <div className="timer-circle">
          <div className="timer-inner">
            <span>{timeLeft}</span>
          </div>
        </div>
        
        <div className="question-counter">
          {currentQuestion + 1}/{questions.length}
        </div>
      </div>

      <div className="question-section">
        <div className="question-box">
          <h3>{currentQ.question}</h3>
        </div>
        
        <div className="options-grid">
          {currentQ.options.map((option, index) => (
            <button
              key={index}
              className={`option-btn ${selectedAnswer === option ? 'selected' : ''} ${
                showResult ? (option === currentQ.correctAnswer ? 'correct' : 
                selectedAnswer === option ? 'wrong' : '') : ''
              }`}
              onClick={() => handleAnswerSelect(option)}
              disabled={showResult}
            >
              <span className="option-letter">{String.fromCharCode(65 + index)}</span>
              <span className="option-text">{option}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="game-controls">
        <div className="lifelines">
          <button 
            className={`lifeline-btn ${!lifelines.fiftyFifty ? 'used' : ''}`}
            onClick={useFiftyFifty}
            disabled={!lifelines.fiftyFifty}
          >
            50:50
          </button>
          <button 
            className={`lifeline-btn ${!lifelines.skip ? 'used' : ''}`}
            onClick={useSkip}
            disabled={!lifelines.skip}
          >
            Skip
          </button>
          <button 
            className={`lifeline-btn ${!lifelines.hint ? 'used' : ''}`}
            onClick={useHint}
            disabled={!lifelines.hint}
          >
            Hint
          </button>
        </div>
        
        <div className="action-buttons">
          <button 
            className="submit-btn"
            onClick={submitAnswer}
            disabled={!selectedAnswer || showResult}
          >
            Final Answer
          </button>
          <button className="quit-btn" onClick={() => { stopAudio(); onExit(); }}>Quit Game</button>
        </div>
      </div>
    </div>
  );
};

export default QuizGame;